import {
  FunctionComponent,
  MouseEvent,
  useCallback,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import Clear from "@mui/icons-material/Clear";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "../utils/management-toolbar.constants";
import {
  fitManagementFilterSelectChips,
  normalizeManagementFilterSelectIds,
  resolveManagementFilterOptionLabel,
  splitManagementFilterSelectChips,
  type ManagementFilterSelectOption
} from "../utils/management-filter-select.utils";
import { ManagementToolbarField } from "./ManagementToolbarField";

type ManagementFilterSelectBaseProps = {
  label: string;
  labelId: string;
  options: ManagementFilterSelectOption[];
  allLabel: string;
  disabled?: boolean;
  "data-testid"?: string;
  formatOptionLabel?: (option: ManagementFilterSelectOption) => string;
};

type ManagementFilterSelectMultiProps = ManagementFilterSelectBaseProps & {
  multiple: true;
  value: string[];
  onChange: (ids: string[]) => void;
};

type ManagementFilterSelectSingleProps = ManagementFilterSelectBaseProps & {
  multiple?: false;
  /** Empty string = all (Pattern A). */
  value: string;
  onChange: (id: string) => void;
};

export type ManagementFilterSelectProps =
  | ManagementFilterSelectMultiProps
  | ManagementFilterSelectSingleProps;

/**
 * Fixed-width toolbar select: chips stay on one line; overflow collapses to +N only when needed.
 */
const managementFilterSelectSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    width: "100%",
    minWidth: 0,
    minHeight: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
    height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
    alignItems: "center"
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
    height: "100%",
    py: 0,
    pr: 4
  }
} as const;

/**
 * Shared Management filter select: Pattern A (empty = all, no All row, plain MenuItems).
 * Selected values render as chips; +N appears only when chips do not fit the field width.
 */
export const ManagementFilterSelect: FunctionComponent<ManagementFilterSelectProps> = props => {
  const { t } = useTranslation();
  const {
    label,
    labelId,
    options,
    allLabel,
    disabled = false,
    formatOptionLabel,
    "data-testid": dataTestId
  } = props;

  const isMultiple = props.multiple === true;
  const selectedIds = isMultiple ? props.value : props.value === "" ? [] : [props.value];
  const [visibleCount, setVisibleCount] = useState(selectedIds.length);
  const viewportRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);

  const recomputeVisibleCount = useCallback(() => {
    const viewport = viewportRef.current;
    const measure = measureRef.current;
    if (!viewport || !measure || selectedIds.length === 0) {
      setVisibleCount(selectedIds.length);
      return;
    }

    const availableWidth = viewport.clientWidth;
    // jsdom / pre-layout: keep all chips visible until we have a real width.
    if (availableWidth <= 0) {
      setVisibleCount(selectedIds.length);
      return;
    }

    const chipEls = [
      ...measure.querySelectorAll<HTMLElement>("[data-management-filter-measure-chip='true']")
    ];
    if (chipEls.length !== selectedIds.length) {
      setVisibleCount(selectedIds.length);
      return;
    }

    const moreEl = measure.querySelector<HTMLElement>(
      "[data-management-filter-measure-more='true']"
    );
    const chipWidths = chipEls.map(element => element.offsetWidth);
    const fit = fitManagementFilterSelectChips({
      chipWidths,
      availableWidth,
      moreChipWidth: moreEl?.offsetWidth ?? 48
    });
    setVisibleCount(fit.visibleCount);
  }, [selectedIds.length]);

  useLayoutEffect(() => {
    recomputeVisibleCount();
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      recomputeVisibleCount();
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [recomputeVisibleCount, selectedIds, disabled, formatOptionLabel, options, t]);

  const removeId = (idToRemove: string) => {
    if (props.multiple === true) {
      props.onChange(props.value.filter(id => id !== idToRemove));
      return;
    }
    props.onChange("");
  };

  const renderChip = (id: string, measure = false) => (
    <Chip
      key={measure ? `measure-${id}` : id}
      size="small"
      label={resolveManagementFilterOptionLabel(id, options, formatOptionLabel)}
      onDelete={
        disabled
          ? undefined
          : event => {
              event.preventDefault();
              event.stopPropagation();
              removeId(id);
            }
      }
      onMouseDown={event => {
        event.preventDefault();
        event.stopPropagation();
      }}
      data-management-filter-measure-chip={measure ? "true" : undefined}
      data-testid={!measure && dataTestId ? `${dataTestId}-chip-${id}` : undefined}
      sx={{
        flexShrink: 0,
        maxWidth: 160,
        "& .MuiChip-label": {
          overflow: "hidden",
          textOverflow: "ellipsis"
        }
      }}
    />
  );

  const renderClosedValue = () => {
    if (selectedIds.length === 0) {
      return (
        <Typography
          component="span"
          color="text.secondary"
          variant="body2"
          noWrap
          sx={{ minWidth: 0 }}
        >
          {allLabel}
        </Typography>
      );
    }

    const { visibleIds, hiddenCount } = splitManagementFilterSelectChips(selectedIds, visibleCount);

    return (
      <Box
        ref={viewportRef}
        data-management-filter-chip-viewport="true"
        sx={{ width: "100%", minWidth: 0, position: "relative" }}
      >
        <Box
          ref={measureRef}
          aria-hidden
          sx={{
            position: "absolute",
            visibility: "hidden",
            pointerEvents: "none",
            display: "flex",
            flexWrap: "nowrap",
            gap: 0.5,
            whiteSpace: "nowrap"
          }}
        >
          {selectedIds.map(id => renderChip(id, true))}
          <Chip
            size="small"
            label={t("_management_moreSelectedFilters", { count: 9 })}
            data-management-filter-measure-more="true"
            sx={{ flexShrink: 0 }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            gap: 0.5,
            width: "100%",
            minWidth: 0,
            overflow: "hidden"
          }}
        >
          {visibleIds.map(id => renderChip(id))}
          {hiddenCount > 0 ? (
            <Chip
              size="small"
              label={t("_management_moreSelectedFilters", { count: hiddenCount })}
              data-testid={dataTestId ? `${dataTestId}-chip-more` : undefined}
              sx={{ flexShrink: 0 }}
              onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
            />
          ) : null}
        </Box>
      </Box>
    );
  };

  const handleChange = (event: SelectChangeEvent<string | string[]>) => {
    const nextIds = normalizeManagementFilterSelectIds(event.target.value);
    if (props.multiple === true) {
      props.onChange(nextIds);
      return;
    }
    props.onChange(nextIds[0] ?? "");
  };

  const handleClear = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (props.multiple === true) {
      props.onChange([]);
      return;
    }
    props.onChange("");
  };

  const showClear = selectedIds.length > 0 && !disabled;

  return (
    <ManagementToolbarField label={label} labelId={labelId}>
      <FormControl fullWidth size="small" sx={managementFilterSelectSx} disabled={disabled}>
        <Select
          labelId={labelId}
          multiple={isMultiple}
          displayEmpty
          value={props.value}
          onChange={handleChange}
          renderValue={renderClosedValue}
          data-testid={dataTestId}
          disabled={disabled}
          endAdornment={
            showClear ? (
              <InputAdornment position="end" sx={{ mr: 2 }}>
                <IconButton
                  size="small"
                  aria-label={t("_management_clearFilter")}
                  onMouseDown={event => event.preventDefault()}
                  onClick={handleClear}
                  data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
                  edge="end"
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined
          }
        >
          {options.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {formatOptionLabel ? formatOptionLabel(option) : option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ManagementToolbarField>
  );
};
